import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import CloseIcon from "@mui/icons-material/Close";
import {Box, Chip, ClickAwayListener, Stack, Typography} from "@mui/material";
import type {LexicalEditor, LexicalNode, SerializedLexicalNode, Spread} from "lexical";
import {
    $createParagraphNode,
    $createTextNode,
    $getNodeByKey,
    $getRoot,
    $isTextNode,
    DecoratorNode,
    type NodeKey,
} from "lexical";
import type {PromptSlotKind} from "../../utils/promptPlaceholders";
import {inferSlotKind} from "../../utils/promptPlaceholders";
import {menuTitleForSlotKind} from "./entityMenuOptions";
import {EntityPlaceholderContext} from "./entityPlaceholderContext.tsx";
import {CustomPopper} from "../../../../components/customPopper/CustomPopper.tsx";
import type {SupplierPickerSelection, SupplierPickerVariant} from "../AgentTemplates/menus/SupplierTree.ts";
import {isSlashPickerKind} from "./entityPickerMenuComponents.tsx";
import {ENTITY_PICKER_MENU_COMPONENTS} from "./EntityPickerMenu.tsx";

/* Lexical node + decorator: fast-refresh expects components-only files. */

/* eslint-disable react-refresh/only-export-components */

/** Escapes user text inside `` `...` `` segments in serialized prompts. */
export function escapeEntityPromptValue(value: string): string {
    return value.replace(/\\/g, "\\\\").replace(/`/g, "\\`");
}

/**
 * How resolved entity chips appear in `getTextContent()` / submitted prompts,
 * e.g. `supplier:`Acme Corp`` or `parent_supplier:`BM Holding``.
 */
/** Prompt key sent to the agent (`parent_supplier` is a supplier subcategory, not a slot kind). */
export function entityPromptSegmentKey(
    kind: PromptSlotKind,
    supplierVariant?: SupplierPickerVariant | null,
): string {
    if (kind === "supplier" && supplierVariant === "parent") {
        return "parent_supplier";
    }
    return kind;
}

export function serializeEntityPromptSegment(
    kind: PromptSlotKind,
    value: string,
    supplierVariant?: SupplierPickerVariant | null,
): string {
    return `${entityPromptSegmentKey(kind, supplierVariant)}:\`${escapeEntityPromptValue(value)}\``;
}

/** Reverses {@link escapeEntityPromptValue} for display / markdown conversion. */
export function unescapeEntityPromptValue(value: string): string {
    return value.replace(/\\\\/g, "\\").replace(/\\`/g, "`");
}

const ENTITY_PROMPT_SEGMENT_RE =
    /(parent_supplier|supplier|category|material|plant|cost_center):`((?:\\.|[^`\\])*)`/g;

const ENTITY_PROMPT_KEY_LABELS: Record<string, string> = {
    parent_supplier: "Parent supplier",
    supplier: "Supplier",
    category: "Category",
    material: "Material",
    plant: "Plant",
    cost_center: "Cost center",
};

/**
 * Converts submitted prompt text (entity `key:\`value\`` segments) into markdown for the chat UI.
 */
export function userMessageContentToMarkdown(content: string): string {
    return content.replace(
        ENTITY_PROMPT_SEGMENT_RE,
        (_match, key: string, raw: string) => {
            const label = ENTITY_PROMPT_KEY_LABELS[key] ?? key;
            const value = unescapeEntityPromptValue(raw);
            return `**${label}:** \`${value}\``;
        },
    );
}

const DRAFT_ENTITY_TOKEN_RE =
    /\[([^\]]+)\]|(parent_supplier|supplier|category|material|plant|cost_center):`((?:\\.|[^`\\])*)`/g;

/** Whether draft text includes `[slot]` placeholders or resolved `kind:\`value\`` entity segments. */
export function draftTextContainsEntities(text: string): boolean {
    DRAFT_ENTITY_TOKEN_RE.lastIndex = 0;
    return DRAFT_ENTITY_TOKEN_RE.test(text);
}

function entityMetaFromSerializedKey(key: string): {
    kind: PromptSlotKind;
    label: string;
    supplierVariant: SupplierPickerVariant | null;
} {
    if (key === "parent_supplier") {
        return {
            kind: "supplier",
            label: ENTITY_PROMPT_KEY_LABELS.parent_supplier,
            supplierVariant: "parent",
        };
    }
    return {
        kind: key as PromptSlotKind,
        label: ENTITY_PROMPT_KEY_LABELS[key] ?? key,
        supplierVariant: null,
    };
}

function $appendDraftParagraphContent(
    p: ReturnType<typeof $createParagraphNode>,
    text: string,
): void {
    const re = new RegExp(DRAFT_ENTITY_TOKEN_RE.source, "g");
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
        if (m.index > lastIndex) {
            p.append($createTextNode(text.slice(lastIndex, m.index)));
        }
        if (m[1] !== undefined) {
            p.append($createEntityPlaceholderNodeFromLabel(m[1].trim()));
        } else if (m[2] !== undefined && m[3] !== undefined) {
            const {kind, label, supplierVariant} = entityMetaFromSerializedKey(m[2]);
            const value = unescapeEntityPromptValue(m[3]);
            p.append($createEntityPlaceholderNode(label, kind, value, supplierVariant));
        }
        lastIndex = m.index + m[0].length;
    }
    if (lastIndex < text.length) {
        p.append($createTextNode(text.slice(lastIndex)));
    }
    if (p.getChildrenSize() === 0) {
        p.append($createTextNode(text));
    }
}

/**
 * Restores persisted draft text into the editor, rendering entity chips for
 * `[label]` placeholders and resolved `kind:\`value\`` segments.
 */
export function $insertDraftText(text: string): void {
    const root = $getRoot();
    root.clear();
    const p = $createParagraphNode();
    $appendDraftParagraphContent(p, text);
    root.append(p);
    const last = p.getLastChild();
    if (!$isTextNode(last)) {
        const spacer = $createTextNode("");
        p.append(spacer);
        spacer.select();
    } else {
        last.selectEnd();
    }
}

export type SerializedEntityPlaceholderNode = Spread<
    {
        label: string;
        kind: PromptSlotKind;
        value: string | null;
        supplierVariant?: SupplierPickerVariant | null;
    },
    SerializedLexicalNode
>;

export class EntityPlaceholderNode extends DecoratorNode<React.ReactElement> {
    __label: string;
    __kind: PromptSlotKind;
    __value: string | null;
    /** When `kind` is `supplier`, `parent` serializes as `parent_supplier:`… */
    __supplierVariant: SupplierPickerVariant | null;

    static getType(): string {
        return "entity-placeholder";
    }

    static clone(node: EntityPlaceholderNode): EntityPlaceholderNode {
        return new EntityPlaceholderNode(
            node.__label,
            node.__kind,
            node.__value,
            node.__supplierVariant,
            node.__key,
        );
    }

    constructor(
        label: string,
        kind: PromptSlotKind,
        value: string | null,
        supplierVariant: SupplierPickerVariant | null = null,
        key?: NodeKey,
    ) {
        super(key);
        this.__label = label;
        this.__kind = kind;
        this.__value = value;
        this.__supplierVariant =
            kind === "supplier" ? supplierVariant : null;
    }

    createDOM(): HTMLElement {
        const span = document.createElement("span");
        span.style.display = "inline-block";
        span.style.verticalAlign = "baseline";
        return span;
    }

    updateDOM(): boolean {
        return false;
    }

    isInline(): boolean {
        return true;
    }

    isKeyboardSelectable(): boolean {
        return true;
    }

    getTextContent(): string {
        if (this.__value != null && this.__value !== "") {
            return `${serializeEntityPromptSegment(this.__kind, this.__value, this.__supplierVariant)} `;
        }
        return `[${this.__label}]`;
    }

    getSlotLabel(): string {
        return this.__label;
    }

    getSlotKind(): PromptSlotKind {
        return this.__kind;
    }

    getSlotValue(): string | null {
        return this.__value;
    }

    getSupplierVariant(): SupplierPickerVariant | null {
        return this.__supplierVariant;
    }

    setValue(value: string): void {
        const writable = this.getWritable();
        writable.__value = value;
    }

    exportJSON(): SerializedEntityPlaceholderNode {
        return {
            type: "entity-placeholder",
            version: 1,
            label: this.__label,
            kind: this.__kind,
            value: this.__value,
            supplierVariant: this.__supplierVariant,
        };
    }

    static importJSON(
        serialized: SerializedEntityPlaceholderNode,
    ): EntityPlaceholderNode {
        return $createEntityPlaceholderNode(
            serialized.label,
            serialized.kind,
            serialized.value,
            serialized.supplierVariant ?? null,
        );
    }

    decorate(editor: LexicalEditor): React.ReactElement {
        return (
            <EntityPlaceholderDecorator
                nodeKey={this.getKey()}
                label={this.__label}
                kind={this.__kind}
                value={this.__value}
                supplierVariant={this.__supplierVariant}
                editor={editor}
            />
        );
    }
}

export function $createEntityPlaceholderNode(
    label: string,
    kind: PromptSlotKind,
    value: string | null,
    supplierVariant: SupplierPickerVariant | null = null,
): EntityPlaceholderNode {
    return new EntityPlaceholderNode(label, kind, value, supplierVariant);
}

export function $createEntityPlaceholderNodeFromLabel(
    label: string,
): EntityPlaceholderNode {
    return new EntityPlaceholderNode(label, inferSlotKind(label), null);
}

/**
 * Replaces the editor root with a paragraph containing text + entity nodes for each `[label]`.
 * Kept in this module next to {@link EntityPlaceholderNode} so Lexical’s registered `klass`
 * matches the constructor used when appending nodes (avoids duplicate-module / HMR class mismatch).
 *
 * @returns Lexical key of the first entity node, or `null` if none.
 */
export function $insertPromptWithEntities(prompt: string): string | null {
    const root = $getRoot();
    root.clear();
    const p = $createParagraphNode();
    const re = /\[([^\]]+)\]/g;
    let lastIndex = 0;
    let firstEntityKey: string | null = null;
    let m: RegExpExecArray | null;
    while ((m = re.exec(prompt)) !== null) {
        if (m.index > lastIndex) {
            p.append($createTextNode(prompt.slice(lastIndex, m.index)));
        }
        const node = $createEntityPlaceholderNodeFromLabel(m[1].trim());
        if (firstEntityKey === null) {
            firstEntityKey = node.getKey();
        }
        p.append(node);
        lastIndex = m.index + m[0].length;
    }
    if (lastIndex < prompt.length) {
        p.append($createTextNode(prompt.slice(lastIndex)));
    }
    if (p.getChildrenSize() === 0) {
        p.append($createTextNode(prompt));
    }
    root.append(p);
    const last = p.getLastChild();

    if (!$isTextNode(last)) {
        const spacer = $createTextNode("");
        p.append(spacer);
        spacer.select();
    } else {
        last.selectEnd();
    }
    return firstEntityKey;
}

export function $isEntityPlaceholderNode(
    node: LexicalNode | null | undefined,
): node is EntityPlaceholderNode {
    return node instanceof EntityPlaceholderNode;
}

export function isSupplierPickerSelectionArray(
    values: unknown,
): values is SupplierPickerSelection[] {
    const first = Array.isArray(values) ? values[0] : undefined;
    return (
        typeof first === "object" &&
        first !== null &&
        "variant" in first &&
        "name" in first &&
        (first.variant === "parent" || first.variant === "child")
    );
}

function createEntityNodesFromSupplierSelections(
    selections: SupplierPickerSelection[],
): EntityPlaceholderNode[] {
    return selections.map((s) =>
        $createEntityPlaceholderNode("Supplier", "supplier", s.name, s.variant),
    );
}


/** Replaces a contiguous entity group with parent/child supplier chips. */
export function $replaceEntityGroupWithSupplierSelections(
    nodeKey: NodeKey,
    selections: SupplierPickerSelection[],
): void {
    const group = $findContiguousSupplierEntityGroup(nodeKey);
    if (group.length === 0) {
        return;
    }
    if (selections.length === 0) {
        for (const n of group) {
            n.remove();
        }
        return;
    }
    const newNodes = createEntityNodesFromSupplierSelections(selections);
    const first = group[0]!;
    first.replace(newNodes[0]!);
    let tail = newNodes[0]!;
    for (let i = 1; i < newNodes.length; i++) {
        tail.insertAfter(newNodes[i]!);
        tail = newNodes[i]!;
    }
    for (let i = 1; i < group.length; i++) {
        group[i]!.remove();
    }
}

/** Replaces a single entity chip with parent/child supplier chips (slash insert). */
export function $replaceSingleEntityWithSupplierSelections(
    nodeKey: NodeKey,
    selections: SupplierPickerSelection[],
): void {
    const node = $getNodeByKey(nodeKey);
    if (!$isEntityPlaceholderNode(node)) {
        return;
    }
    if (selections.length === 0) {
        node.remove();
        return;
    }
    const newNodes = createEntityNodesFromSupplierSelections(selections);
    node.replace(newNodes[0]!);
    let tail = newNodes[0]!;
    for (let i = 1; i < newNodes.length; i++) {
        tail.insertAfter(newNodes[i]!);
        tail = newNodes[i]!;
    }
}

/** Adjacent supplier chips from one picker session (parent + child subcategories). */
export function $findContiguousSupplierEntityGroup(
    nodeKey: NodeKey,
): EntityPlaceholderNode[] {
    const self = $getNodeByKey(nodeKey);
    if (!$isEntityPlaceholderNode(self) || self.getSlotKind() !== "supplier") {
        return [];
    }
    const parent = self.getParent();
    if (!parent) {
        return [self];
    }
    const children = parent.getChildren();
    const idx = children.indexOf(self);
    if (idx < 0) {
        return [self];
    }
    let start = idx;
    while (start > 0) {
        const prev = children[start - 1];
        if (
            !$isEntityPlaceholderNode(prev) ||
            prev.getSlotKind() !== "supplier"
        ) {
            break;
        }
        start--;
    }
    let end = idx;
    while (end < children.length - 1) {
        const next = children[end + 1];
        if (
            !$isEntityPlaceholderNode(next) ||
            next.getSlotKind() !== "supplier"
        ) {
            break;
        }
        end++;
    }
    return children
        .slice(start, end + 1)
        .filter((n): n is EntityPlaceholderNode => $isEntityPlaceholderNode(n));
}

/** All consecutive entity chips with the same slot label + kind (one “selection group”). */
export function $findContiguousEntityGroup(
    nodeKey: NodeKey,
): EntityPlaceholderNode[] {
    const self = $getNodeByKey(nodeKey);
    if (!$isEntityPlaceholderNode(self)) {
        return [];
    }
    const label = self.getSlotLabel();
    const kind = self.getSlotKind();
    const parent = self.getParent();
    if (!parent) {
        return [self];
    }
    const children = parent.getChildren();
    const idx = children.indexOf(self);
    if (idx < 0) {
        return [self];
    }
    let start = idx;
    while (start > 0) {
        const prev = children[start - 1];
        if (
            !$isEntityPlaceholderNode(prev) ||
            prev.getSlotLabel() !== label ||
            prev.getSlotKind() !== kind
        ) {
            break;
        }
        start--;
    }
    let end = idx;
    while (end < children.length - 1) {
        const next = children[end + 1];
        if (
            !$isEntityPlaceholderNode(next) ||
            next.getSlotLabel() !== label ||
            next.getSlotKind() !== kind
        ) {
            break;
        }
        end++;
    }
    return children
        .slice(start, end + 1)
        .filter((n): n is EntityPlaceholderNode => $isEntityPlaceholderNode(n));
}

/**
 * Replaces a contiguous group of entity chips starting at `nodeKey`.
 * Empty `values` removes every chip in the group (used when clearing selection).
 */
export function $replaceEntityGroupWithValues(
    nodeKey: NodeKey,
    label: string,
    kind: PromptSlotKind,
    values: string[],
): void {
    const group = $findContiguousEntityGroup(nodeKey);
    if (group.length === 0) {
        return;
    }
    if (values.length === 0) {
        for (const n of group) {
            n.remove();
        }
        return;
    }
    const newNodes = values.map((v) =>
        $createEntityPlaceholderNode(label, kind, v),
    );
    const first = group[0]!;
    first.replace(newNodes[0]!);
    let tail = newNodes[0]!;
    for (let i = 1; i < newNodes.length; i++) {
        tail.insertAfter(newNodes[i]!);
        tail = newNodes[i]!;
    }
    for (let i = 1; i < group.length; i++) {
        group[i]!.remove();
    }
}

/**
 * Replaces only the entity chip at `nodeKey` (used for new slash-command inserts).
 * Does not expand to adjacent chips of the same kind.
 */
export function $replaceSingleEntityPlaceholderWithValues(
    nodeKey: NodeKey,
    label: string,
    kind: PromptSlotKind,
    values: string[],
): void {
    const node = $getNodeByKey(nodeKey);
    if (!$isEntityPlaceholderNode(node)) {
        return;
    }
    if (values.length === 0) {
        node.remove();
        return;
    }
    const newNodes = values.map((v) =>
        $createEntityPlaceholderNode(label, kind, v),
    );
    node.replace(newNodes[0]!);
    let tail = newNodes[0]!;
    for (let i = 1; i < newNodes.length; i++) {
        tail.insertAfter(newNodes[i]!);
        tail = newNodes[i]!;
    }
}

export function $replaceEntityPlaceholderWithValues(
    nodeKey: NodeKey,
    label: string,
    kind: PromptSlotKind,
    values: string[],
): void {
    $replaceEntityGroupWithValues(nodeKey, label, kind, values);
}

function entityChipLabel(
    label: string,
    kind: PromptSlotKind,
    supplierVariant: SupplierPickerVariant | null,
): string {
    if (kind === "supplier" && supplierVariant === "parent") {
        return ENTITY_PROMPT_KEY_LABELS.parent_supplier;
    }
    return label;
}

type DecoratorProps = {
    nodeKey: NodeKey;
    label: string;
    kind: PromptSlotKind;
    value: string | null;
    supplierVariant: SupplierPickerVariant | null;
    editor: LexicalEditor;
};

function EntityPlaceholderDecorator({
                                        nodeKey,
                                        label,
                                        kind,
                                        value,
                                        supplierVariant,
                                        editor,
                                    }: DecoratorProps) {
    const {autoOpenNodeKey, clearAutoOpen, openPlacement} = useContext(
        EntityPlaceholderContext,
    );
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [menuInitialValues, setMenuInitialValues] = useState<string[]>([]);
    const didAutoOpen = useRef(false);

    const displayLabel = entityChipLabel(label, kind, supplierVariant);
    const chipLabel = value ? (
        <Stack direction="row" spacing={1}>
            <Typography fontSize={13}>{`${displayLabel}:`}</Typography>
            <Typography fontWeight={500} fontSize={13}>
                {value}
            </Typography>
        </Stack>
    ) : (
        `Select ${displayLabel}...`
    );

    const readGroupValuesForMenu = useCallback(() => {
        let names: string[] = [];
        editor.getEditorState().read(() => {
            const g = kind === "supplier"
                ? $findContiguousSupplierEntityGroup(nodeKey)
                : $findContiguousEntityGroup(nodeKey);
            names = g
                .map((n) => n.getSlotValue())
                .filter((v): v is string => Boolean(v && v.trim()));
        });
        return names;
    }, [editor, nodeKey, kind]);

    useEffect(() => {
        if (autoOpenNodeKey === nodeKey && value === null && !didAutoOpen.current) {
            const t = window.setTimeout(() => {
                didAutoOpen.current = true;
                setMenuInitialValues(readGroupValuesForMenu());
                setOpen(true);
                clearAutoOpen();
            }, 0);
            return () => window.clearTimeout(t);
        }
        return undefined;
    }, [autoOpenNodeKey, nodeKey, value, clearAutoOpen, readGroupValuesForMenu]);

    const applySelection = (option: string | string[] | SupplierPickerSelection[]) => {
        editor.update(() => {
            if (typeof option === "string") {
                const node = $getNodeByKey(nodeKey);
                if ($isEntityPlaceholderNode(node)) {
                    node.setValue(option);
                }
            } else if (isSupplierPickerSelectionArray(option)) {
                $replaceEntityGroupWithSupplierSelections(nodeKey, option);
            } else if (Array.isArray(option)) {
                $replaceEntityPlaceholderWithValues(nodeKey, label, kind, option);
            }
        });
        setOpen(false);
    };

    const menuTitle = menuTitleForSlotKind(kind, label);
    let chipPickerMenu: React.ReactNode = null;
    if (isSlashPickerKind(kind)) {
        const Menu = ENTITY_PICKER_MENU_COMPONENTS[kind];
        chipPickerMenu = (
            <Menu
                key={`${kind}-${menuInitialValues.join("\u001f")}`}
                onApply={(vals) => applySelection(vals)}
                menuTitle={menuTitle}
                initialSelectedValues={menuInitialValues}
            />
        );
    }

    return (
        <ClickAwayListener
            onClickAway={() => {
                if (open) {
                    setOpen(false);
                }
            }}
        >
            <Box
                component="span"
                ref={setAnchorEl}
                sx={{display: "inline-flex", verticalAlign: "middle", mx: 0.25}}
            >
                <Chip
                    size="small"
                    variant={"outlined"}
                    label={chipLabel}
                    onDelete={(event) => {
                        event.stopPropagation();
                        editor.update(() => {
                            const node = $getNodeByKey(nodeKey);
                            if ($isEntityPlaceholderNode(node)) {
                                node.remove();
                            }
                        });
                        setOpen(false);
                        queueMicrotask(() => editor.focus());
                    }}
                    deleteIcon={
                        <CloseIcon
                            aria-label={`Remove ${displayLabel}`}
                            fontSize="small"
                            onMouseDown={(e) => {
                                e.stopPropagation();
                            }}
                        />
                    }
                    onMouseDown={(e) => {
                        if (
                            (e.target as HTMLElement).closest(".MuiChip-deleteIcon") != null
                        ) {
                            return;
                        }
                        e.preventDefault();
                        if (open) {
                            setOpen(false);
                            return;
                        }
                        setMenuInitialValues(readGroupValuesForMenu());
                        setOpen(true);
                    }}
                    sx={{
                        height: 26,
                        borderWidth: "2px",
                        borderStyle: value ? "solid" : "dashed",
                        borderColor: "grey.100",
                        m: 0.5,
                        cursor: "pointer",
                        "& .MuiChip-deleteIcon": {
                            margin: "0 4px 0 -2px",
                        },
                        "& .MuiChip-label": {
                            px: 1,
                            whiteSpace: "normal",
                            textAlign: "left",
                        },
                    }}
                />
                <CustomPopper
                    open={open}
                    anchorEl={anchorEl}
                    keepMounted
                    placement={openPlacement.placement}
                >
                    {chipPickerMenu}
                </CustomPopper>
            </Box>
        </ClickAwayListener>
    );
}
